import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { CtaBlock } from "../../components/CtaBlock";
import { RelatedArticles } from "../../components/RelatedArticles";

const ARTICLE = {
	title: "楽天市場 商品画像 推奨サイズ・形式・容量 2026年版まとめ",
	description:
		"楽天市場の商品画像要件（推奨700×700px、最大5MB、テキスト占有率20%以内）を2026年版で詳しく解説。よくあるエラーと対処法も。",
	publishDate: "2026-03-16",
	slug: "rakuten-product-image-size-2026",
};

const ogImageUrl = `https://og.fucina.casa/api/og?title=${encodeURIComponent(ARTICLE.title)}&description=${encodeURIComponent(ARTICLE.description)}&locale=ja`;

export default function RakutenProductImageSize2026() {
	return (
		<>
			<Helmet>
				<title>{ARTICLE.title} | しろぬきや</title>
				<meta name="description" content={ARTICLE.description} />
				<meta property="og:title" content={ARTICLE.title} />
				<meta property="og:description" content={ARTICLE.description} />
				<meta
					property="og:url"
					content={`https://shironukiya.fucina.casa/blog/${ARTICLE.slug}`}
				/>
				<meta property="og:type" content="article" />
				<meta property="og:image" content={ogImageUrl} />
				<meta name="twitter:card" content="summary_large_image" />
				<meta name="twitter:title" content={ARTICLE.title} />
				<meta name="twitter:description" content={ARTICLE.description} />
				<meta name="twitter:image" content={ogImageUrl} />
				<link
					rel="canonical"
					href={`https://shironukiya.fucina.casa/blog/${ARTICLE.slug}`}
				/>
				<script type="application/ld+json">
					{JSON.stringify({
						"@context": "https://schema.org",
						"@type": "Article",
						headline: ARTICLE.title,
						description: ARTICLE.description,
						datePublished: ARTICLE.publishDate,
						publisher: {
							"@type": "Organization",
							name: "しろぬきや",
							url: "https://shironukiya.fucina.casa",
						},
					})}
				</script>
			</Helmet>

			<div className="max-w-3xl mx-auto px-4 py-10">
				<nav className="text-sm text-gray-500 mb-6">
					<Link to="/" className="hover:underline">
						ホーム
					</Link>
					{" > "}
					<Link to="/blog" className="hover:underline">
						ブログ
					</Link>
					{" > "}
					<span>{ARTICLE.title}</span>
				</nav>

				<article>
					<h1 className="text-2xl font-bold mb-4">{ARTICLE.title}</h1>
					<p className="text-gray-500 text-sm mb-8">
						更新日: {ARTICLE.publishDate}
					</p>

					<p className="text-gray-700 mb-6">
						楽天市場に出品していて「画像のアップロードでエラーが出る」「どのサイズで作ればいいかわからない」と悩んでいませんか？
						楽天市場の画像規格はAmazonと異なるルールがあり、特に
						<strong>テキスト占有率20%ルール</strong>は見落としがちです。
						2026年版の最新要件をまとめました。
					</p>

					<section>
						<h2 className="text-xl font-semibold mt-8 mb-4">
							楽天市場の商品画像要件（2026年）
						</h2>
						<div className="overflow-x-auto mb-4">
							<table className="w-full text-sm border-collapse border border-gray-200">
								<thead>
									<tr className="bg-gray-50">
										<th className="border border-gray-200 px-4 py-2 text-left">
											項目
										</th>
										<th className="border border-gray-200 px-4 py-2 text-left">
											要件
										</th>
									</tr>
								</thead>
								<tbody>
									<tr>
										<td className="border border-gray-200 px-4 py-2">
											最小サイズ
										</td>
										<td className="border border-gray-200 px-4 py-2">
											500×500px 以上
										</td>
									</tr>
									<tr className="bg-gray-50">
										<td className="border border-gray-200 px-4 py-2">
											推奨サイズ
										</td>
										<td className="border border-gray-200 px-4 py-2">
											<strong>700×700px 以上</strong>
										</td>
									</tr>
									<tr>
										<td className="border border-gray-200 px-4 py-2">
											最大サイズ
										</td>
										<td className="border border-gray-200 px-4 py-2">
											3840×3840px 以内
										</td>
									</tr>
									<tr className="bg-gray-50">
										<td className="border border-gray-200 px-4 py-2">
											ファイル形式
										</td>
										<td className="border border-gray-200 px-4 py-2">
											JPEG（.jpg）、PNG（.png）、GIF（アニメーション不可）
										</td>
									</tr>
									<tr>
										<td className="border border-gray-200 px-4 py-2">
											最大ファイルサイズ
										</td>
										<td className="border border-gray-200 px-4 py-2">
											5MB 以内
										</td>
									</tr>
									<tr className="bg-gray-50">
										<td className="border border-gray-200 px-4 py-2">背景色</td>
										<td className="border border-gray-200 px-4 py-2">
											白基調または写真背景（枠線使用禁止）
										</td>
									</tr>
									<tr>
										<td className="border border-gray-200 px-4 py-2">
											テキスト占有率
										</td>
										<td className="border border-gray-200 px-4 py-2">
											<strong>第1・SKU画像は20%以内</strong>
										</td>
									</tr>
									<tr className="bg-gray-50">
										<td className="border border-gray-200 px-4 py-2">
											最大登録枚数
										</td>
										<td className="border border-gray-200 px-4 py-2">20枚</td>
									</tr>
								</tbody>
							</table>
						</div>
					</section>

					<section>
						<h2 className="text-xl font-semibold mt-8 mb-4">
							楽天独自ルール①：テキスト占有率20%以内
						</h2>
						<p className="text-gray-700 mb-4">
							楽天市場では、
							<strong>第1商品画像とSKU画像のテキスト占有率が20%以内</strong>
							でなければなりません。
							これはAmazonにはないルールで、見落とすと審査で弾かれる原因になります。
						</p>
						<p className="text-gray-700 mb-4">
							テキスト占有率の計算方法は以下のとおりです：
						</p>
						<ol className="list-decimal list-inside space-y-2 text-gray-700 text-sm mb-4">
							<li>画像を10×10の格子（100マス）に分割する</li>
							<li>
								テキスト・ロゴ・キャッチコピー・イラスト（文字を含む）が入っているマスを数える
							</li>
							<li>20マス（20%）以内に収める</li>
						</ol>
						<div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
							<p className="text-sm text-blue-800">
								<strong>💡 チェックツール</strong>
								：楽天RMS内に「テキスト占有率確認ツール」が無料で用意されています。画像をアップロードすると自動計算してくれるので、投稿前に必ず確認しましょう。
							</p>
						</div>
					</section>

					<section>
						<h2 className="text-xl font-semibold mt-8 mb-4">
							楽天独自ルール②：枠線・飾り枠の使用禁止
						</h2>
						<p className="text-gray-700 mb-4">
							楽天市場では商品画像への<strong>枠線・飾り枠の使用が禁止</strong>
							されています。
							「映える」画像を作ろうと白枠や黒枠を付けると規約違反になります。
							背景は白基調か写真背景（生活感のある自然な背景）のどちらかにしましょう。
						</p>
					</section>

					<section>
						<h2 className="text-xl font-semibold mt-8 mb-4">
							よくあるエラーと対処法
						</h2>
						<div className="space-y-4">
							<div className="border rounded-lg p-4">
								<p className="font-semibold text-gray-800 mb-1">
									「画像サイズが規定外です」
								</p>
								<p className="text-sm text-gray-700">
									→
									500×500px未満の場合に発生。最低でも700×700pxで作り直してください。
								</p>
							</div>
							<div className="border rounded-lg p-4">
								<p className="font-semibold text-gray-800 mb-1">
									「ファイルサイズが大きすぎます」
								</p>
								<p className="text-sm text-gray-700">
									→
									5MBを超えている場合。JPEG品質を85%程度に下げるか、画像圧縮ツールを使用してください。
								</p>
							</div>
							<div className="border rounded-lg p-4">
								<p className="font-semibold text-gray-800 mb-1">
									「テキスト占有率が20%を超えています」
								</p>
								<p className="text-sm text-gray-700">
									→
									商品名や販促コピーを画像内に入れすぎている場合。テキストを減らすか、第2枚目以降に移動してください。
								</p>
							</div>
						</div>
					</section>

					<section>
						<h2 className="text-xl font-semibold mt-8 mb-4">
							しろぬきやで解決する方法
						</h2>
						<p className="text-gray-700 mb-4">
							楽天市場の要件（700×700px以上・白背景・5MB以内）に合わせた画像作成を、毎回手動でやるのは手間がかかります。
							<strong>しろぬきや</strong>
							を使えば、AI背景除去＋楽天市場の推奨サイズへの自動リサイズがブラウザ上で一括完了します。
							画像はサーバーに送信しないためプライバシーも安全。無料で1日5枚まで利用できます。
						</p>
						<CtaBlock />
					</section>

					<RelatedArticles current={ARTICLE.slug} />
				</article>
			</div>
		</>
	);
}
